/*
 * Figma `121:443` — OPS-03 · People · Staffing + Shifts.
 *
 * `TableTemplate`: the coverage panel above the roster, with the member you opened in a drawer.
 *
 * Built against the part of that frame the product has. The brief asks staffing to show "coverage
 * gaps, shifts, and protected contact details". Two of three are backed:
 *
 * - **Coverage gaps** — real, and derived per *department* rather than per hour. A department with
 *   nobody active, or with nobody marked as lead, is a gap somebody can act on before the day.
 * - **Protected contact details** — real, and the reason this screen is careful. The list carries
 *   masked values only; the per-member call is the one thing in the product that returns a real
 *   phone number, so revealing is deliberate, one person at a time, and never merged into the row.
 * - **Shifts** — not backed. There is no roster: `OpsStaffSummaryDto` carries a status and
 *   departments, and no endpoint accepts an hour. The frame's hourly coverage chart (`121:648`) is
 *   not drawn. Reported in the resume record rather than faked with invented hours.
 */
import { useState } from 'react';
import {
  Alert, Button, Card, ConfirmDialog, Drawer, FormDialog, KpiCard, MeterRow, SearchField, Select,
  StatusBadge, TableTemplate, TextField, Toggle,
} from '../..';
import type { BadgeTone, Column } from '../..';
import type { Destination } from '../../destinations';
import type { OpsDepartmentDto, OpsStaffStatus, OpsStaffSummaryDto } from '../../../api';
import type { OpsPeopleState } from './useOpsPeople';
import { useOpsPeople } from './useOpsPeople';

const STATUS_LABEL: Record<OpsStaffStatus, string> = {
  active: 'พร้อมปฏิบัติงาน', standby: 'สำรอง', off_duty: 'ไม่อยู่เวร',
};
const STATUS_TONE: Record<OpsStaffStatus, BadgeTone> = {
  active: 'success', standby: 'warning', off_duty: 'neutral',
};

export function OpsPeopleScreen({ destination, state, permitted, role }: {
  destination: Destination;
  state: OpsPeopleState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    staff, departments, coverage, loading, loadError, error, busyId,
    revealed, revealBusy, reveal, clearRevealed, create, update, setStatus, remove, reload,
    createDepartment, renameDepartment, removeDepartment, setDepartments,
  } = state;
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ displayName: '', jobTitle: '', phone: '', email: '' });
  const [addingDept, setAddingDept] = useState(false);
  const [deptName, setDeptName] = useState('');
  const [renaming, setRenaming] = useState<OpsDepartmentDto | null>(null);
  const [renameTo, setRenameTo] = useState('');
  const [deletingDept, setDeletingDept] = useState<OpsDepartmentDto | null>(null);

  /** The member's set, as the PUT wants it, with one department's membership or lead flag changed. */
  const departmentsAfter = (
    member: OpsStaffSummaryDto,
    departmentId: string,
    change: { member?: boolean; isLead?: boolean },
  ) => {
    const current = member.departments.map((d) => ({ departmentId: d.departmentId, isLead: d.isLead }));
    const at = current.findIndex((d) => d.departmentId === departmentId);
    if (change.member === false) return current.filter((d) => d.departmentId !== departmentId);
    if (at === -1) return [...current, { departmentId, isLead: change.isLead ?? false }];
    return current.map((d) => (d.departmentId === departmentId
      ? { ...d, isLead: change.isLead ?? d.isLead }
      : d));
  };
  const [openId, setOpenId] = useState<string | null>(null);

  const open = openId ? staff.find((s) => s.id === openId) ?? null : null;
  const rows = staff.filter((s) => {
    if (!query.trim()) return true;
    const needle = query.trim().toLowerCase();
    return `${s.displayName} ${s.jobTitle}`.toLowerCase().includes(needle);
  });

  const active = staff.filter((s) => s.status === 'active').length;
  const uncovered = coverage.filter((c) => c.active === 0);
  const leaderless = coverage.filter((c) => c.total > 0 && !c.hasLead);
  const unassigned = staff.filter((s) => s.departments.length === 0);

  const columns: Column<OpsStaffSummaryDto>[] = [
    {
      key: 'name',
      header: 'ชื่อ',
      cell: (s) => (
        <span className="jt-cell-stack">
          <span>{s.displayName}</span>
          <span className="jt-cell-sub">{s.jobTitle || 'ไม่ระบุตำแหน่ง'}</span>
        </span>
      ),
    },
    {
      key: 'dept',
      header: 'ฝ่าย',
      cell: (s) => (s.departments.length === 0
        ? <StatusBadge tone="warning">ยังไม่ได้เข้าฝ่าย</StatusBadge>
        : (
          <span className="jt-inline-actions">
            {s.departments.map((d) => (
              <StatusBadge key={d.departmentId} tone={d.isLead ? 'accent' : 'neutral'}>
                {d.isLead ? `${d.name} · หัวหน้า` : d.name}
              </StatusBadge>
            ))}
          </span>
        )),
    },
    {
      key: 'contact',
      header: 'ติดต่อ',
      width: '190px',
      // Masked, always. The real value needs a deliberate reveal from the drawer.
      cell: (s) => <span className="jt-mono jt-cell-sub">{s.phoneMasked ?? s.emailMasked ?? '—'}</span>,
    },
    {
      key: 'status',
      header: 'สถานะ',
      width: '180px',
      cell: (s) => <StatusBadge tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</StatusBadge>,
    },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null, onRetry: reload, permitted, role }}
      kpis={[
        <KpiCard key="all" label="ทีมงานทั้งหมด" value={staff.length.toLocaleString('th-TH')} />,
        <KpiCard key="active" label="พร้อมปฏิบัติงาน" value={active.toLocaleString('th-TH')} tone={active > 0 ? 'positive' : 'danger'} />,
        <KpiCard key="gap" label="ฝ่ายที่ไม่มีคนพร้อม" value={uncovered.length.toLocaleString('th-TH')} tone={uncovered.length > 0 ? 'danger' : undefined} />,
        <KpiCard key="lead" label="ฝ่ายที่ไม่มีหัวหน้า" value={leaderless.length.toLocaleString('th-TH')} tone={leaderless.length > 0 ? 'warning' : undefined} />,
      ]}
      summary={
        <>
          {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
          {uncovered.length > 0 && (
            <Alert tone="danger" title={`${uncovered.length} ฝ่ายยังไม่มีคนพร้อมปฏิบัติงาน`}>
              {uncovered.map((c) => c.department.name).join(' · ')} — ถ้าเกิดเรื่องในฝ่ายเหล่านี้ ยังไม่มีใครรับ
            </Alert>
          )}
          {unassigned.length > 0 && (
            <Alert tone="warning" title={`${unassigned.length} คนยังไม่ได้เข้าฝ่ายใด`}>
              คนที่ไม่ได้อยู่ฝ่ายไหนจะไม่ถูกนับในความพร้อมของฝ่ายใดเลย
            </Alert>
          )}

          <Card
            title="ความพร้อมรายฝ่าย"
            sub="นับจากสถานะของสมาชิกแต่ละฝ่าย — ระบบนี้ยังไม่มีตารางเวรรายชั่วโมง"
          >
            {coverage.length === 0 && (
              <p className="jt-field-hint">ยังไม่ได้ตั้งฝ่ายใด ๆ — สร้างฝ่ายแรกเพื่อเริ่มแบ่งทีมงาน</p>
            )}
            {coverage.map((c) => (
              /* Named so the row and its two actions are one thing to point at, in CSS and in a test. */
              <div key={c.department.id} className="jt-dept-row">
                <MeterRow
                  label={c.department.name}
                  value={`พร้อม ${c.active} · สำรอง ${c.standby} · ไม่อยู่เวร ${c.offDuty}`}
                  percent={c.total === 0 ? 0 : (c.active / c.total) * 100}
                  tone={c.active === 0 ? 'danger' : c.active < c.standby ? 'warning' : 'positive'}
                  caption={c.hasLead ? undefined : 'ยังไม่มีหัวหน้าฝ่าย'}
                />
                {permitted && (
                  <div className="jt-inline-actions">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyId === c.department.id}
                      onClick={() => { setRenaming(c.department); setRenameTo(c.department.name); }}
                    >
                      เปลี่ยนชื่อ
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyId === c.department.id}
                      onClick={() => setDeletingDept(c.department)}
                    >
                      ลบฝ่าย
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {permitted && (
              <div className="jt-inline-actions">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => { setDeptName(''); setAddingDept(true); }}
                >
                  ＋ สร้างฝ่าย
                </Button>
              </div>
            )}
          </Card>
        </>
      }
      toolbar={
        <>
          <SearchField value={query} onChange={setQuery} label="ค้นหาทีมงาน" placeholder="ชื่อ / ตำแหน่ง" />
          {permitted && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => { setDraft({ displayName: '', jobTitle: '', phone: '', email: '' }); setAdding(true); }}
            >
              ＋ เพิ่มทีมงาน
            </Button>
          )}
        </>
      }
      table={{
        label: 'ทีมงาน',
        columns,
        rows,
        rowKey: (s) => s.id,
        filtered: query.trim().length > 0,
        onClearFilters: () => setQuery(''),
        emptyTitle: 'ยังไม่มีทีมงาน',
        onRowClick: (s) => { setOpenId(s.id); clearRevealed(); },
        activeRowKey: openId,
        card: (s) => (
          <span className="jt-cell-stack">
            <span>{s.displayName}</span>
            <span className="jt-cell-sub">{s.jobTitle || 'ไม่ระบุตำแหน่ง'}</span>
            <StatusBadge tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</StatusBadge>
          </span>
        ),
      }}
      drawer={
        <>
        {/*
          * The screen had no control that added anybody — `useOpsPeople` has carried `create`
          * since the rebuild and nothing called it. On an event with no staff yet that made this
          * destination a dead end: it groups people by department and there was no way to get a
          * first person into it.
          */}
        <FormDialog
          open={adding}
          onClose={() => setAdding(false)}
          onSubmit={() => {
            void create({
              displayName: draft.displayName.trim(),
              jobTitle: draft.jobTitle.trim() || undefined,
              // `null` clears; an empty box means "not given" rather than "cleared".
              phone: draft.phone.trim() || null,
              email: draft.email.trim() || null,
            }).then(() => setAdding(false));
          }}
          title="เพิ่มทีมงาน"
          description="เบอร์และอีเมลถูกปิดบังในรายชื่อ และเห็นค่าจริงได้เมื่อกดดูรายคนเท่านั้น"
          submitLabel="เพิ่มทีมงาน"
          error={error || null}
        >
          <TextField
            label="ชื่อที่ใช้เรียก"
            value={draft.displayName}
            onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
          />
          <TextField
            label="ตำแหน่ง"
            value={draft.jobTitle}
            onChange={(e) => setDraft({ ...draft, jobTitle: e.target.value })}
            hint="เว้นว่างได้"
          />
          <TextField
            label="เบอร์โทร"
            value={draft.phone}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            hint="เว้นว่างได้"
          />
          <TextField
            label="อีเมล"
            value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            hint="เว้นว่างได้"
          />
        </FormDialog>

        <FormDialog
          open={addingDept}
          onClose={() => setAddingDept(false)}
          onSubmit={() => { void createDepartment(deptName.trim()).then(() => setAddingDept(false)); }}
          title="สร้างฝ่าย"
          description="ฝ่ายคือหน่วยที่ความพร้อมถูกนับ — ทีมงานที่ไม่ได้อยู่ฝ่ายไหนจะไม่ถูกนับในฝ่ายใดเลย"
          submitLabel="สร้างฝ่าย"
          error={error || null}
        >
          <TextField
            label="ชื่อฝ่าย"
            value={deptName}
            onChange={(e) => setDeptName(e.target.value)}
            hint="เช่น เวที · ต้อนรับ · ความปลอดภัย"
          />
        </FormDialog>

        <FormDialog
          open={!!renaming}
          onClose={() => setRenaming(null)}
          onSubmit={() => {
            const target = renaming;
            if (target) void renameDepartment(target, renameTo.trim()).then(() => setRenaming(null));
          }}
          title={`เปลี่ยนชื่อฝ่าย ${renaming?.name ?? ''}`}
          submitLabel="บันทึกชื่อใหม่"
          error={error || null}
        >
          <TextField label="ชื่อฝ่าย" value={renameTo} onChange={(e) => setRenameTo(e.target.value)} />
        </FormDialog>

        <ConfirmDialog
          open={!!deletingDept}
          onClose={() => setDeletingDept(null)}
          onConfirm={() => {
            const target = deletingDept;
            setDeletingDept(null);
            if (target) void removeDepartment(target);
          }}
          title={`ลบฝ่าย ${deletingDept?.name ?? ''}`}
          confirmLabel="ลบฝ่ายนี้"
          consequences={[
            'ทีมงานที่อยู่ฝ่ายนี้จะไม่ถูกลบ แต่จะกลายเป็นคนที่ยังไม่ได้เข้าฝ่ายใด',
            'ความพร้อมของฝ่ายนี้จะหายไปจากรายการข้างบน',
            'งานที่เคยผูกกับฝ่ายนี้ต้องหาเจ้าของใหม่',
          ]}
        />

        <Drawer
          open={!!open}
          onClose={() => { setOpenId(null); clearRevealed(); }}
          title={open?.displayName ?? ''}
          footer={open && (
            <Button variant="ghost" disabled={busyId === open.id} onClick={() => void remove(open)}>ลบออกจากทีม</Button>
          )}
        >
          {open && (
            <>
              <Card title="สถานะและตำแหน่ง">
                <Select
                  label="สถานะ"
                  value={open.status}
                  disabled={busyId === open.id}
                  options={(Object.keys(STATUS_LABEL) as OpsStaffStatus[]).map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
                  onChange={(e) => void setStatus(open, e.target.value as OpsStaffStatus)}
                />
                <TextField
                  label="ตำแหน่ง"
                  value={open.jobTitle}
                  onChange={(e) => void update(open, { jobTitle: e.target.value })}
                />
              </Card>

              {/*
                * Which departments this person is in, and which of them they lead. The endpoint
                * replaces the whole set, so each toggle sends the set as it will be rather than
                * the one row that changed.
                */}
              <Card
                title="ฝ่ายที่สังกัด"
                sub={departments.length === 0
                  ? 'ยังไม่มีฝ่ายให้เลือก — สร้างฝ่ายก่อนจากรายการความพร้อมด้านซ้าย'
                  : 'คนหนึ่งอยู่ได้หลายฝ่าย และเป็นหัวหน้าได้มากกว่าหนึ่งฝ่าย'}
              >
                {open.departments.length === 0 && departments.length > 0 && (
                  <p className="jt-field-hint">
                    ยังไม่ได้เข้าฝ่ายใด — คนนี้จะไม่ถูกนับในความพร้อมของฝ่ายไหนเลย
                  </p>
                )}
                {departments.map((d) => {
                  const here = open.departments.find((x) => x.departmentId === d.id);
                  return (
                    <div key={d.id}>
                      <Toggle
                        label={d.name}
                        checked={!!here}
                        disabled={busyId === open.id}
                        onChange={(e) => void setDepartments(
                          open,
                          departmentsAfter(open, d.id, { member: e.target.checked }),
                        )}
                      />
                      {here && (
                        <Toggle
                          label={`เป็นหัวหน้าฝ่าย ${d.name}`}
                          checked={here.isLead}
                          disabled={busyId === open.id}
                          onChange={(e) => void setDepartments(
                            open,
                            departmentsAfter(open, d.id, { isLead: e.target.checked }),
                          )}
                          consequence="ฝ่ายที่ไม่มีหัวหน้าจะถูกทักไว้ในรายการความพร้อม"
                        />
                      )}
                    </div>
                  );
                })}
              </Card>

              <Card
                title="ข้อมูลติดต่อ"
                sub="รายการปิดข้อมูลไว้เสมอ — ดูค่าเต็มได้ทีละคนเมื่อจำเป็น"
              >
                {revealed && revealed.id === open.id ? (
                  <>
                    <ul className="jt-order-lines">
                      <li><span>เบอร์โทร</span><span className="jt-mono">{revealed.phone ?? '—'}</span></li>
                      <li><span>อีเมล</span><span className="jt-mono">{revealed.email ?? '—'}</span></li>
                    </ul>
                    <div className="jt-inline-actions">
                      <Button size="sm" variant="ghost" onClick={clearRevealed}>ปิดข้อมูลกลับ</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="jt-field-hint jt-mono">
                      {open.phoneMasked ?? '—'}{open.emailMasked ? ` · ${open.emailMasked}` : ''}
                    </p>
                    <div className="jt-inline-actions">
                      <Button size="sm" variant="secondary" disabled={revealBusy} onClick={() => void reveal(open.id)}>
                        {revealBusy ? 'กำลังเปิด…' : 'ดูข้อมูลติดต่อเต็ม'}
                      </Button>
                    </div>
                  </>
                )}
              </Card>

            </>
          )}
        </Drawer>
        </>
      }
    />
  );
}

export function OpsPeopleDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useOpsPeople(token, eventSlug);
  return <OpsPeopleScreen destination={destination} state={state} permitted={permitted} role={role} />;
}
